import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import CardMedia from "@mui/material/CardMedia";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PageviewIcon from "@mui/icons-material/Pageview";
import EditIcon from "@mui/icons-material/Edit";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import NoteAddIcon from "@mui/icons-material/NoteAdd";

import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import Switch from "@mui/material/Switch";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";
import qs from "query-string";
import { useForm, Controller } from "react-hook-form";
import * as XLSX from "xlsx";

import CreateProductModal from "pages/unionwear/components/CreateProductModal";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import ImportExcelModal from "pages/unionwear/components/ImportExcelModal";
import ProductInformationModal from "pages/unionwear/components/ProductInformationModal";

const ProductTable = ({ sharedView = false }) => {
  const [data, setData] = useState([]);

  const [createProductVisible, setCreateProductVisible] = useState(false);
  const [editProductVisible, setEditProductVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({});

  const [deleteVisible, setDeleteVisible] = useState(false);
  const [importExcelVisible, setImportExcelVisible] = useState(false);
  const [productResultVisible, setProductResultVisible] = useState(false);

  const [materials, setMaterials] = useState([]);
  const [excelData, setExcelData] = useState([]);

  const [loading, setLoading] = useState(false);

  let location = useLocation();
  const navigate = useNavigate();

  const uploadExcelRef = useRef(null);
  const user = useContext(AccountContext);

  useEffect(() => {
    retrieveProducts();
    retrieveMaterial();
  }, []);

  const retrieveProducts = async () => {
    try {
      const { data } = await api.uwProduct.list({
        order: "-updated_at",
      });

      setData(data?.uProducts || []);
    } catch (err) {
      toast.error(`Retrieve product list failed!`);
      console.log(err);
    }
  };

  const retrieveMaterial = async () => {
    try {
      const { data } = await api.materialLibrary.list({
        order: "-updated_at",
      });
      setMaterials(data?.materials || []);
    } catch (err) {
      toast.error(`Retrieve material list failed!`);
      console.log(err);
    }
  };

  const handleDeleteProduct = async (product) => {
    try {
      await api.uwProduct.delete(product.id);
      setDeleteVisible(false);
      retrieveProducts();

      toast.success(`${product.name} has been removed successfully!`);
    } catch (err) {
      toast.error(`${product.name} could not be removed!`);
      console.log(err);
    }
  };

  const getProductTableRow = (item) => {
    const images = item?.content?.images || [];

    return {
      key: item.id,
      name: item?.name,
      code: item?.code,
      images: images,
      crown_shape: item?.content?.crown_shape,
      front_panels_fabric: item?.content?.front_panels_fabric,
      backs_and_sides_fabric: item?.content?.backs_and_sides_fabric,
      visor_fabric: item?.content?.visor_fabric,
      button_fabric: item?.content?.button_fabric,
      closure_type: item?.content?.closure_type,
      closure_color: item?.content?.closure_color,
      num_eyelets: item?.content?.num_eyelets,
      crown_stitch_color: item?.content?.crown_stitch_color,
      visor_stitch_color: item?.content?.visor_stitch_color,
      baseball_stitch_color: item?.content?.baseball_stitch_color,
      eyelet_color: item?.content?.eyelet_color,
      num_visor_stitches: item?.content?.num_visor_stitches,
      visor_curve_or_flat: item?.content?.visor_curve_or_flat,
      sandwich_color: item?.content?.sandwich_color,
      trim_color: item?.content?.trim_color,
      description: item?.content?.description,
      created_at: item.created_at,
      updated_at: item.updated_at,
      action: item,
    };
  };

  const columns = [
    {
      Header: "Image",
      accessor: "images",
      align: "center",
      Cell: ({ row, value }) => {
        return value && value.length > 0 ? (
          <img src={value[0]["url"]} style={{ width: 50, height: 50 }} />
        ) : null;
      },
    },
    {
      Header: "Name",
      accessor: "name",
      align: "center",
      Cell: ({ row, value }) => {
        return <div style={{ maxWidth: 160 }}>{value}</div>;
      },
    },
    {
      Header: "Code",
      accessor: "code",
    },
    {
      Header: "Backs and sides fabric",
      accessor: "backs_and_sides_fabric",
    },
    {
      Header: "Visor fabric",
      accessor: "visor_fabric",
    },
    {
      Header: "Crown Shape",
      accessor: "crown_shape",
    },
    {
      Header: "Closure Type",
      accessor: "closure_type",
    },
    {
      Header: "Last Edit",
      accessor: "updated_at",
      Cell: ({ row, value }) => {
        return value ? moment(value).format("MM/DD/YY") : "";
      },
    },
    {
      Header: "More",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => (
        <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Tooltip title="Edit">
            <SuiBox>
              <EditIcon
                fontSize={"small"}
                color="dark"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProduct(value);
                  setEditProductVisible(true);
                }}
              />{" "}
            </SuiBox>
          </Tooltip>
          |{" "}
          <Tooltip title="View results">
            <SuiBox>
              <ManageSearchIcon
                fontSize={"small"}
                color="dark"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProduct(value);
                  setProductResultVisible(true);
                }}
              />{" "}
            </SuiBox>
          </Tooltip>
          |
          <Tooltip title="Delete">
            <SuiBox>
              <DeleteForeverRoundedIcon
                fontSize={"small"}
                color="error"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProduct(value);
                  setDeleteVisible(true);
                }}
              />{" "}
            </SuiBox>
          </Tooltip>
        </SuiBox>
      ),
    },
  ];

  const refresh = () => {
    retrieveProducts();
  };

  const rows = data.map((item) => getProductTableRow(item));

  const handleRowClick = (row) => {
    //setEditProductVisible(true);
    //setSelectedProduct(row?.original.action);
    navigate(`/unionwear/product/${row?.original.key}`);
  };

  const formatData = (data, headerMap, key) =>
    headerMap[key] != undefined ? data[headerMap[key]] : undefined;

  const handleImportExcelMaterial = async (data) => {
    if (!data || data.length < 0) {
      handleClose();
      return;
    }

    let headerMap = {};
    for (let i = 0; i < data[0].length; i++) {
      let key = data[0][i].trim().toLowerCase().replaceAll("'", "").replaceAll(" ", "_");
      if (key == "description") {
        headerMap["name"] = i;
      } else if (key == "sku") {
        headerMap["code"] = i;
      } else if (key == "#_eyelets") {
        headerMap["num_eyelets"] = i;
      } else if (key == "#_visor_stitches") {
        headerMap["num_visor_stitches"] = i;
      } else if (key == "front_panels_fabric" || key == "front_panel_fabric") {
        headerMap["front_panels_fabric"] = i;
      } else {
        headerMap[key] = i;
      }
    }

    let rows = [];
    let metaKeys = [
      "crown_shape",
      "front_panels_fabric",
      "backs_and_sides_fabric",
      "visor_fabric",
      "button_fabric",
      "closure_type",
      "closure_color",
      "num_eyelets",
      "crown_stitch_color",
      "visor_stitch_color",
      "baseball_stitch_color",
      "eyelet_color",
      "num_visor_stitches",
      "visor_curve_or_flat",
      "sandwich_color",
      "trim_color",
      "description",
    ];

    let imageKeys = ["3q_image", "back_image", "virtual", "swatches"];

    for (let i = 1; i < data.length; i++) {
      let name = formatData(data[i], headerMap, "name");
      if (!name) {
        continue;
      }

      let images = imageKeys.map((x) => ({
        url: formatData(data[i], headerMap, x),
        type: "image/jpg",
        name: x,
      }));

      let temp = {};

      for (let j = 0; j < metaKeys.length; j++) {
        temp[metaKeys[j]] = formatData(data[i], headerMap, metaKeys[j]);
      }

      let payload = {
        name: name,
        code: formatData(data[i], headerMap, "code"),
        content: {
          ...temp,
          images: images,
        },
      };

      if (i != 0) {
        rows.push(payload);
      }
    }

    try {
      const created = await api.uwProduct.bulkCreate({ data: rows });
      refresh();
      toast.success("Data has been imported Successfully");
    } catch (err) {
      console.log(err);
      toast.error(err?.message ? err?.message : "Failed to import data!");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setLoading(true);
    const reader = new FileReader();

    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      setExcelData(workbook);
      setImportExcelVisible(true);
      setLoading(false);
    };

    reader.onerror = () => {
      setLoading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <SuiBox>
      <Backdrop sx={{ zIndex: (theme) => 99999 }} open={loading}>
        <CircularProgress color="white" sx={{ mr: 1 }} size={32} />{" "}
        <SuiTypography variant="h6" color="white">
          Loading......
        </SuiTypography>
      </Backdrop>
      <SuiBox display="flex" justifyContent="space-between" pt={2} px={2}>
        <SuiButton
          variant="gradient"
          color={"dark"}
          onClick={() => {
            setCreateProductVisible(true);
          }}
        >
          New Product
        </SuiButton>
        <SuiButton
          variant="gradient"
          color={"info"}
          onClick={() => {
            if (uploadExcelRef?.current) {
              uploadExcelRef.current.click();
              uploadExcelRef.current.value = null;
            }
          }}
        >
          Upload
        </SuiButton>
      </SuiBox>

      <Card sx={{ p: 3 }}>
        <DataTable
          table={{ columns, rows: rows }}
          entriesPerPage={{ defaultValue: 20 }}
          noEndBorder
          handleRowClick={handleRowClick}
          canSearch
        />
      </Card>
      {createProductVisible ? (
        <CreateProductModal
          handleClose={() => setCreateProductVisible(false)}
          visible={createProductVisible}
          materials={materials}
          isEdit={false}
          handleRefresh={refresh}
        />
      ) : null}
      {editProductVisible && selectedProduct ? (
        <CreateProductModal
          handleClose={() => setEditProductVisible(false)}
          visible={editProductVisible}
          isEdit={true}
          materials={materials}
          handleRefresh={refresh}
          product={selectedProduct}
        />
      ) : null}

      {deleteVisible && selectedProduct ? (
        <Prompt
          heading="Delete Product"
          text={`Are you sure you want to delete: ${selectedProduct.name}`}
          visible={deleteVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => handleDeleteProduct(selectedProduct)}
          handleClose={() => setDeleteVisible(false)}
        />
      ) : null}

      {importExcelVisible && excelData ? (
        <ImportExcelModal
          handleClose={() => setImportExcelVisible(false)}
          visible={importExcelVisible}
          handleOk={handleImportExcelMaterial}
          excelData={excelData}
        />
      ) : null}
      {productResultVisible && selectedProduct ? (
        <ProductInformationModal
          handleClose={() => setProductResultVisible(false)}
          visible={productResultVisible}
          product={selectedProduct}
        />
      ) : null}

      <input
        type="file"
        accept=".xlsx, .xls"
        onChange={handleFileUpload}
        ref={uploadExcelRef}
        style={{ display: "none" }}
      />
    </SuiBox>
  );
};

export default ProductTable;
